<script lang="ts" setup>
// import
import {
  ref,
  computed,
  defineProps,
  defineEmits,
  onMounted,
  onUnmounted,
  nextTick,
  watch
} from "vue";

import { createPopper, Instance } from "@popperjs/core";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { authStore } from "@/store/auth";
import { ClosedTooltipPopupSelector } from "@/types/auth/ClosedTooltipPopup";

// Type
type TooltipPosition = "top" | "bottom";

// prop、emits定義
const props = defineProps({
  /**
   * 吹き出しの位置
   * 'top' または 'bottom'
   */
  position: {
    type: String as () => TooltipPosition,
    default: "bottom",
    validator: (value: string) => ["top", "bottom"].includes(value)
  },
  /**
   * ターゲット要素のセレクタ
   * 指定された要素の中心に向けて吹き出しが表示されます
   */
  targetSelector: {
    type: String as () => ClosedTooltipPopupSelector,
    required: true
  }
});

const emits = defineEmits(["close"]);

// refやcomputedなど
const tooltipContainer = ref<HTMLElement | null>(null);
const popperInstance = ref<Instance | null>(null);
const targetExists = ref(false);

const isVisible = computed(() => {
  return (
    // ログイン中のユーザーに対してのみ表示
    authStore.existToken &&
    // ターゲットが存在するか
    targetExists.value &&
    // 閉じた吹き出しポップアップのリストが初期化済みか
    authStore.isInitializedClosedTooltipPopupList &&
    // 閉じた吹き出しポップアップのリストにターゲットセレクタが含まれていないか
    !authStore.hasClosedTooltipPopup(props.targetSelector)
  );
});

// ライフサイクルフック
onMounted(() => {
  targetExists.value = hasTarget();
  if (isVisible.value) {
    nextTick(() => {
      initializePopper();
    });
  }
});

onUnmounted(() => {
  if (isVisible.value) {
    // 吹き出しがアンマウントされるときに表示状態であれば、
    // ユーザーが明示的に閉じなくても、そのポップアップを「既読」として記録する
    // これにより、ページ遷移などでポップアップが閉じられた場合でも状態が保持される
    authStore.addClosedTooltipPopup(props.targetSelector);
  }
  if (popperInstance.value) {
    popperInstance.value.destroy();
  }
  window.removeEventListener("scroll", handleScroll);
});

watch(isVisible, newValue => {
  if (newValue) {
    nextTick(initializePopper);
  } else if (popperInstance.value) {
    popperInstance.value.destroy();
    popperInstance.value = null;
  }
});

watch(
  () => props.targetSelector,
  () => {
    targetExists.value = hasTarget();
    if (isVisible.value) {
      if (popperInstance.value) {
        popperInstance.value.destroy();
      }
      nextTick(initializePopper);
    }
  }
);

watch(
  () => props.position,
  () => {
    if (isVisible.value && popperInstance.value) {
      popperInstance.value.destroy();
      nextTick(initializePopper);
    }
  }
);

// 関数群
/**
 * ターゲット要素が存在するかどうか
 */
function hasTarget(): boolean {
  if (!props.targetSelector) {
    return false;
  }
  return !!document.querySelector(props.targetSelector);
}

/**
 * Popperの初期化
 */
function initializePopper() {
  if (!props.targetSelector || !tooltipContainer.value || !targetExists.value) {
    return;
  }

  const targetElement = document.querySelector(props.targetSelector);
  if (!targetElement) return;

  popperInstance.value = createPopper(targetElement, tooltipContainer.value, {
    placement: props.position,
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 12]
        }
      },
      {
        name: "arrow",
        options: {
          element: tooltipContainer.value?.querySelector(".tooltip-arrow"),
          padding: 5
        }
      },
      {
        name: "computeStyles",
        options: {
          gpuAcceleration: false
        }
      },
      {
        name: "preventOverflow",
        options: {
          padding: { top: 10, bottom: 10, left: 10, right: 10 },
          // 位置の自動変更を無効化
          altAxis: false,
          // 位置固定
          altBoundary: true
        }
      },
      // 位置を固定するための追加モディファイア
      {
        name: "flip",
        options: {
          fallbackPlacements: [], // 代替位置を使用しない
          allowedAutoPlacements: [props.position] // 指定した位置のみ許可
        }
      }
    ],
    strategy: "fixed" // 固定位置戦略を使用
  });

  // スクロールイベントリスナーを追加
  window.addEventListener("scroll", handleScroll);

  // ポップアップの位置を更新
  setTimeout(() => {
    if (popperInstance.value) {
      popperInstance.value.update();
    }
  }, 0);
}

/**
 * スクロールイベントハンドラ
 * スクロール時にポップアップの位置を更新
 */
function handleScroll() {
  if (popperInstance.value) {
    popperInstance.value.update();
  }
}

/**
 * 吹き出しを閉じる
 */
function handleClose() {
  authStore.addClosedTooltipPopup(props.targetSelector);
  emits("close");
}
</script>

<template>
  <div
    v-if="isVisible"
    ref="tooltipContainer"
    class="tooltip-container"
    :class="`tooltip-${position}`"
  >
    <div class="tooltip-content">
      <div class="tooltip-inner-layout">
        <div class="tooltip-text-container">
          <slot />
        </div>
        <div class="close-button-container">
          <FeatherIcon
            icon="x"
            size="18"
            class="close-icon"
            @click="handleClose"
          />
        </div>
      </div>
    </div>
    <div class="tooltip-arrow" data-popper-arrow></div>
  </div>
</template>

<style lang="scss" scoped>
.tooltip-container {
  position: absolute;
  z-index: 50;
  width: auto;
  min-width: 300px;
  max-width: 90%;
  height: auto;
  margin: 0 auto;
  pointer-events: auto;
  will-change: transform;
}

.tooltip-content {
  padding: 0;
  color: #fff;
  background-color: rgb(30 30 30 / 90%);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgb(0 0 0 / 15%);
}

.tooltip-inner-layout {
  display: flex;
  align-items: center;
  padding: 0;
  margin: 0;
}

.tooltip-text-container {
  flex-grow: 1;
  padding: 13.5px 0 13.5px 12px;
  margin: 0;
  overflow-x: visible;
  font-size: 14px;
  font-weight: 500;
  line-height: 150%;
  text-align: center;
  letter-spacing: 0%;
  white-space: nowrap;
  vertical-align: middle;
}

.close-button-container {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

.close-icon {
  color: #fff;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #e0e0e0;
  }
}

.tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
}

.tooltip-bottom .tooltip-arrow {
  top: 0;
  left: 50%;
  margin-top: -8px;
  margin-left: -8px;
  border-right: 8px solid transparent;
  border-bottom: 8px solid rgb(30 30 30 / 90%);
  border-left: 8px solid transparent;
}

.tooltip-top .tooltip-arrow {
  bottom: 0;
  left: 50%;
  margin-bottom: -8px;
  margin-left: -8px;
  border-top: 8px solid rgb(30 30 30 / 90%);
  border-right: 8px solid transparent;
  border-left: 8px solid transparent;
}
</style>
